import React, { useEffect, useState } from "react";
import axiosClient from "../../../axios-client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faFileDownload,
  faEdit,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import Navbar from "../Navbar";
import SideBar from "../SideBar";
import { useNavigate } from "react-router-dom";

const UploadReport = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [reports, setReports] = useState([]); // All reports
  const [filteredReports, setFilteredReports] = useState([]); // Reports matching the search term
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false); // For regular report update modal
  const [segmentModalOpen, setSegmentModalOpen] = useState(false); // For segment update modal
  const [selectedReport, setSelectedReport] = useState(null); // Stores the selected report object
  const [searchTerm, setSearchTerm] = useState(""); // Search term for filtering
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);

  // Handle file selection
  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  // Handle file upload for creating a new report
  const handleUpload = async (event) => {
    event.preventDefault();
    if (!file) {
      setMessage("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      setMessage("Uploading..."); // Set a loading message
      const loadingInterval = setInterval(() => {
        setMessage((prev) =>
          prev === "Uploading..." ? "Still Uploading..." : "Uploading..."
        );
      }, 1000);

      const response = await axiosClient.post(
        "reports/upload/titles",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      clearInterval(loadingInterval); // Clear the interval once upload is done
      setMessage(`File uploaded successfully: ${response.data.message}`);
      setFile(null); // Clear the file input
      handleRefresh();
    } catch (error) {
      console.error("Upload error:", error);
      setMessage("Failed to upload the file. Please try again.");
    } finally {
      setUploading(false);
    }
  };
  useEffect(() => {
    const adminToken = localStorage.getItem("Admin_Token");
    if (!adminToken) {
      navigate("/admin/login");
    }
  }, [navigate]);

  // Handle file upload for updating an existing report
  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!file || !selectedReport) {
      setMessage("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const loadingInterval = setInterval(() => {
        setMessage((prev) =>
          prev === "Uploading..." ? "Still Uploading..." : "Uploading..."
        );
      }, 5000);
      const response = await axiosClient.put(
        `reports/upload/reportdata/${selectedReport._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      clearInterval(loadingInterval); // Clear the interval once upload is done

      setMessage(`Report updated successfully: ${response.data.message}`);
      setModalOpen(false); // Close the regular modal
      setFile(null); // Reset file input
    } catch (error) {
      console.error("Update error:", error);
      setMessage("Failed to update the report. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Handle file upload for updating a segment of an existing report
  const handleSegmentUpdate = async (event) => {
    event.preventDefault();
    if (!file || !selectedReport) {
      setMessage("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);
      const response = await axiosClient.put(
        `/reports/upload/segmentdata/${selectedReport._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setMessage(`Segment updated successfully: ${response.data.message}`);
      setSegmentModalOpen(false); // Close the segment update modal
      setFile(null); // Reset file input
    } catch (error) {
      console.error("Segment update error:", error);
      setMessage("Failed to update the segment. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Fetch reports when the component loads
  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await axiosClient.get("/reports/getReport");
        setReports(response.data); // Save full report objects
        setFilteredReports(response.data); // Initialize filteredReports
      } catch (error) {
        console.error("Error fetching reports:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  // Handle search term change
  const handleSearchChange = (event) => {
    const term = event.target.value.toLowerCase();
    const filtered = reports.filter((report) =>
      report.reportTitle.toLowerCase().includes(term)
    );
    setFilteredReports(filtered);
    setSearchTerm(term);
  };

  // Open the modal to update a specific report
  const openModal = (report) => {
    setSelectedReport(report); // Store the full report object
    setModalOpen(true); // Open the regular modal
  };

  // Open the modal to update a report's segment
  const openSegmentModal = (report) => {
    setSelectedReport(report); // Store the full report object
    setSegmentModalOpen(true); // Open the segment update modal
  };

  const handleRefresh = async () => {
    setLoading(true); // Show loading while fetching data
    try {
      const response = await axiosClient.get("/reports/getReport");
      setReports(response.data); // Update the full reports list
      setFilteredReports(response.data); // Update the filtered list as well
      setSearchTerm(""); // Clear the search term
    } catch (error) {
      console.error("Error refreshing reports:", error);
      setMessage("Failed to refresh the reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosClient.delete("/reports/deleteall");
    } catch (error) {
      console.error("Error deleting all reports:", error);
    } finally {
      setDeleteModal(false);
      handleRefresh();
    }
  };

  return (
    <>
      <SideBar />
      {/* <Navbar /> */}
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6 pl-[19rem]">
        {/* Upload Section */}
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold mb-6 text-center">
            Upload Bulk Report
          </h1>

          <form
            // onSubmit={handleUpload}
            className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-8"
          >
            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="file"
              >
                Upload the Reports Title
              </label>
              <input
                type="file"
                id="file"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                className="border rounded w-full py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
              />
            </div>
            <div className="flex justify-between items-center">
              <button
                // type="submit"
                onClick={handleUpload}
                disabled={uploading}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>
              <a
                href="/sampleFile/newbook2.xlsx"
                download="Sample_Report.xlsx"
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
              >
                <FontAwesomeIcon icon={faFileDownload} className="mr-2" />
                Sample Report
              </a>
            </div>
          </form>
        </div>

        {/* Existing Reports Section */}
        <div className="w-full max-w-4xl mt-10">
          <h2 className="text-xl font-bold mb-4 text-center">
            Upload Data in Existing Reports
          </h2>
          {/* Search Box */}
          <div className="flex justify-end mb-4">
            <div className="relative" style={{ width: "20rem" }}>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search reports..."
                className="border rounded w-full py-2 pl-10 pr-3 text-gray-700 focus:outline-none focus:shadow-outline"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-2.5 text-gray-500"
              />
            </div>
            <button
              onClick={handleRefresh}
              className="ml-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
            >
              Refresh
            </button>
            <button
              // onClick={handleDeleteAll}
              onClick={() => setDeleteModal(true)}
              className="ml-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
            >
              Delete All Reports
            </button>
          </div>

          {/* Reports List */}
          <div className="bg-white shadow-md rounded p-4 max-h-96 w-[1000px] overflow-y-auto">
            {loading ? (
              <p className="text-center">Loading...</p>
            ) : filteredReports.length === 0 ? (
              <p className="text-center text-gray-500">No reports found.</p>
            ) : (
              <ul className="space-y-3">
                {filteredReports.map((report) => (
                  <li
                    key={report._id} // Use unique _id as key
                    className="flex justify-between items-center border-b pb-2"
                  >
                    <span className="text-gray-800">{report.reportTitle}</span>
                    <div className="flex space-x-2">
                      {" "}
                      {/* Add a flex container for buttons */}
                      <button
                        onClick={() => openModal(report)}
                        className="bg-blue-500 hover:bg-blue-700 text-white text-sm font-bold py-1 px-3 rounded focus:outline-none flex items-center"
                      >
                        <FontAwesomeIcon icon={faEdit} className="mr-2" />
                        Update
                      </button>
                      <button
                        onClick={() => openSegmentModal(report)}
                        className="bg-green-500 hover:bg-green-700 text-white text-sm font-bold py-1 px-3 rounded focus:outline-none flex items-center"
                      >
                        <FontAwesomeIcon icon={faEdit} className="mr-2" />
                        Update Segment
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Regular Report DeleteAll Modal */}
        {deleteModal && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-md w-full max-w-lg">
              <form onSubmit={handleDeleteAll}>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="file"
                  >
                    Delete All Reports
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteModal(false)}
                    className="bg-gray-500 hover:bg-gray-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                  >
                    {"DeleteAll"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Regular Report Update Modal */}
        {modalOpen && selectedReport && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-md w-full max-w-lg">
              <h3 className="text-lg font-bold mb-4 text-gray-800">
                Update {selectedReport.reportTitle}
              </h3>
              <form onSubmit={handleUpdate}>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="file"
                  >
                    Upload File
                  </label>
                  <input
                    type="file"
                    id="file"
                    accept=".xlsx, .xls"
                    onChange={handleFileChange}
                    className="border rounded w-full py-2 px-3 text-gray-700 focus:outline-none"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <a
                    href="/sampleFile/singlegloblereportnew.xlsx"
                    download="single report data.xlsx"
                    className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
                  >
                    <FontAwesomeIcon icon={faFileDownload} className="mr-2" />
                    Sample Report
                  </a>
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="bg-gray-500 hover:bg-gray-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="bg-blue-500 hover:bg-blue-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Segment Update Modal */}
        {segmentModalOpen && selectedReport && (
          <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-md w-full max-w-lg">
              <h3 className="text-lg font-bold mb-4 text-gray-800">
                Update Segment for {selectedReport.reportTitle}
              </h3>
              <form onSubmit={handleSegmentUpdate}>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="file"
                  >
                    Upload File
                  </label>
                  <input
                    type="file"
                    id="file"
                    accept=".xlsx, .xls"
                    onChange={handleFileChange}
                    className="border rounded w-full py-2 px-3 text-gray-700 focus:outline-none"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <a
                    href="/sampleFile/SegmentData.xlsx"
                    download="Sample_Report.xlsx"
                    className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none"
                  >
                    <FontAwesomeIcon icon={faFileDownload} className="mr-2" />
                    Sample Report
                  </a>
                  <button
                    type="button"
                    onClick={() => setSegmentModalOpen(false)}
                    className="bg-gray-500 hover:bg-gray-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="bg-blue-500 hover:bg-blue-700 text-white text-sm font-bold py-2 px-4 rounded focus:outline-none"
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UploadReport;
